import React, { FunctionComponent, useState, useEffect, useContext, useCallback } from 'react';

import { Text } from '../../../shared/Text';
import { StepperContent } from '../../../shared/Stepper';
import { useForm } from 'react-hook-form';
import { Input } from '../../../shared/Input/Input';
import { InputHolder, InputHolderItem } from '../../../shared/InputHolder';
import { isNumber } from '../../../../utils/validations';
import { formItems, getDefaultValues, transformCalculationDataForApi, fetchLowestInterestRate } from '../form-items';
import { Select } from '../../../shared/Select';
import { Button } from '../../../shared/Button';
import { Radio } from '../../../shared/Radio';
import { FormContext, APIResponse } from '../../../../store/MLI/MLIContext';
import { API } from '../../../../services';

const defaultValues = getDefaultValues();

export const Step1: FunctionComponent = () => {
    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        unregister,
        getValues,
    } = useForm({ mode: 'onChange' });

    const { updateForm, updateData, setIsLoading } = useContext(FormContext);

    const [showExtraFields, setShowExtraFields] = useState(false);

    const scrollToCalculation = () => {
        const calculation = document.querySelector<HTMLDivElement>('.c-mli-calculation');

        if (calculation) {
            window.scrollTo({
                top: calculation.offsetTop,
                behavior: 'smooth',
            });
        }
    };

    const fetchMonthlyCostsByMortgage = useCallback(
        async (data: any, scroll: boolean = false) => {
            setIsLoading({ state: true, message: 'We zijn je maandlasten aan het berekenen'});
            const interestRate = await fetchLowestInterestRate(
                Number(data[formItems.mortgageType]),
                Number(data[formItems.mortageAmount]),
            );
            const transformedData = transformCalculationDataForApi(data, interestRate);
            API.post<APIResponse>('calculator/monthlycostsbymortgage', transformedData)
                .then((res) => {
                    updateData(res);
                    setIsLoading({ state: false, message: ''});
                    if (scroll) {
                        scrollToCalculation();
                    }
                })
                .catch((err) => {
                    console.log(err);
                    setIsLoading({ state: false, message: ''});
                });
        },
        [setIsLoading, updateData],
    );

    useEffect(() => {
        if (!showExtraFields) {
            unregister(['partnerAge', 'partnerYearlyIncome']);
        }
    }, [showExtraFields, unregister]);

    useEffect(() => {
        updateForm(getValues());
    }, [showExtraFields]); // eslint-disable-line

    useEffect(() => {
        fetchMonthlyCostsByMortgage(getValues());
    }, []); // eslint-disable-line

    const onSubmit = (data: any) => fetchMonthlyCostsByMortgage(data, true);

    return (
        <div className="u-padding-top--md">
            <StepperContent>
                <Text as="h3">Bereken je maandlasten van dit huis</Text>
                <form onSubmit={handleSubmit(onSubmit)} onChange={() => updateForm(getValues())}>
                    <InputHolder>
                        <InputHolderItem>
                            <Input
                                label="Gewenst Hypotheekbedrag*"
                                name={formItems.mortageAmount}
                                id={formItems.mortageAmount}
                                placeholder="€"
                                defaultValue={defaultValues[formItems.mortageAmount]}
                                register={register}
                                hasError={errors[formItems.mortageAmount]}
                                validation={{ required: true, validate: isNumber }}
                            />
                            {errors[formItems.mortageAmount] && (
                                <span className="form__error">
                                    Vul een geldig hypotheekbedrag in
                                </span>
                            )}
                        </InputHolderItem>
                    </InputHolder>
										<InputHolder>
											<InputHolderItem>
												<Input
													label="Bruto jaarinkomen*"
													name={formItems.applicantYearlyIncome}
													id={formItems.applicantYearlyIncome}
													placeholder="€"
													defaultValue={defaultValues[formItems.applicantYearlyIncome]}
													register={register}
													hasError={errors[formItems.applicantYearlyIncome]}
													validation={{ required: true, validate: isNumber }}
												/>
												{errors[formItems.applicantYearlyIncome] && (
													<span className="form__error">Vul een geldig jaarinkomen in</span>
												)}
											</InputHolderItem>
										</InputHolder>
                    <InputHolder>
                        <InputHolderItem>
                            <Input
                                label="Leeftijd*"
                                name={formItems.applicantAge}
                                id={formItems.applicantAge}
                                defaultValue={defaultValues[formItems.applicantAge]}
                                type="number"
                                register={register}
                                hasError={errors[formItems.applicantAge]}
                                validation={{ required: true }}
                            />
                        </InputHolderItem>
                    </InputHolder>
										<InputHolder>
											<InputHolderItem>
												<Select
													label="Hypotheekvorm*"
													name={formItems.mortgageType}
													id={formItems.mortgageType}
													register={register}
													validation={{ required: true }}
													options={[
														{
															name: 'Annuïteitenhypotheek',
															value: 4,
														},
														{
															name: 'Lineaire hypotheek',
															value: 9,
														},
													]}
												/>
											</InputHolderItem>
										</InputHolder>
                    <hr />
                    <span className="input__label">Heb je een partner?</span>
                    <InputHolder>
                        <InputHolderItem isSmall={true}>
                            <Radio
                                label="Ja"
                                name={formItems.hasPartnerIncome}
                                id={`${formItems.hasPartnerIncome}-1`}
                                defaultValue={'true'}
                                register={register}
                                validation={{
                                    onChange: (e: any) =>
                                        setShowExtraFields(e.currentTarget.value === 'true'),
                                }}
                            />
                        </InputHolderItem>
                        <InputHolderItem isSmall={true}>
                            <Radio
                                label="Nee"
                                checked={true}
                                name={formItems.hasPartnerIncome}
                                id={`${formItems.hasPartnerIncome}-2`}
                                defaultValue={'false'}
                                register={register}
                            />
                        </InputHolderItem>
                    </InputHolder>

                    {showExtraFields && (
                        <div className="u-margin-top--md">
                            <InputHolder>
                                <InputHolderItem>
                                    <Input
                                        label="Bruto jaarinkomen partner*"
                                        name={formItems.partnerYearlyIncome}
                                        id={formItems.partnerYearlyIncome}
                                        defaultValue={defaultValues[formItems.partnerYearlyIncome]}
                                        placeholder="€"
                                        register={register}
                                        hasError={errors[formItems.partnerYearlyIncome]}
                                        validation={{ required: true, validate: isNumber }}
                                    />
                                    {errors[formItems.partnerYearlyIncome] && (
                                        <span className="form__error">
                                            Vul een geldig jaarinkomen in
                                        </span>
                                    )}
                                </InputHolderItem>
                                <InputHolderItem>
                                    <Input
                                        label="Leeftijd partner*"
                                        name={formItems.partnerAge}
                                        id={formItems.partnerAge}
                                        defaultValue={defaultValues[formItems.partnerAge]}
                                        type="number"
                                        register={register}
                                        hasError={errors[formItems.partnerAge]}
                                        validation={{ required: true }}
                                    />
                                </InputHolderItem>
                            </InputHolder>
                        </div>
                    )}
                    <div className="u-padding-top--md u-padding-bottom--md">
                        <Button label="Bereken" disabled={!isValid} />
                    </div>
                </form>
            </StepperContent>
        </div>
    );
};
